/**
 * MeetingDetailPage
 * Detailed view of a meeting with tabs for MoM, Transcript, JIRA items, and Settings
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, MessageSquare, GitBranch, Settings as SettingsIcon } from 'lucide-react';
import { AppLayout } from '../../components/layout';
import {
  MomViewer,
  TranscriptViewer,
  MomApproval,
  MeetingSettings,
} from '../../components/meetings';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { meetingApi } from '../../services/api';
import type { Meeting, MeetingMinutes, Transcript, MeetingSettings as MeetingSettingsType, ProposedEpic } from '../../types';
import styles from './MeetingDetailPage.module.css';

type TabType = 'mom' | 'transcript' | 'approval' | 'settings';

export const MeetingDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [meeting, setMeeting] = useState<Meeting | null>(null);
  const [mom, setMom] = useState<MeetingMinutes | null>(null);
  const [transcript, setTranscript] = useState<Transcript | null>(null);
  const [settings, setSettings] = useState<MeetingSettingsType | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('mom');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Load meeting data
  const loadMeetingData = useCallback(async () => {
    if (!id) return;

    setIsLoading(true);
    setError(null);

    try {
      const [meetingResponse, momResponse, settingsResponse] = await Promise.all([
        meetingApi.getMeetingById(id),
        meetingApi.getMeetingMom(id).catch(() => null),
        meetingApi.getMeetingSettings().catch(() => null),
      ]);

      if (meetingResponse.success && meetingResponse.data) {
        setMeeting(meetingResponse.data);
      }

      if (momResponse?.success && momResponse.data) {
        setMom(momResponse.data);
      }

      if (settingsResponse?.success && settingsResponse.data) {
        setSettings(settingsResponse.data);
      }
    } catch (err) {
      console.error('Failed to load meeting data:', err);
      setError('Failed to load meeting details. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadMeetingData();
  }, [loadMeetingData]);

  // Load transcript when tab is activated
  const loadTranscript = useCallback(async () => {
    if (!id || transcript) return;

    try {
      const response = await meetingApi.getMeetingTranscript(id);
      if (response.success && response.data) {
        setTranscript(response.data);
      }
    } catch (err) {
      console.error('Failed to load transcript:', err);
    }
  }, [id, transcript]);

  useEffect(() => {
    if (activeTab === 'transcript') {
      loadTranscript();
    }
  }, [activeTab, loadTranscript]);

  // Handle MoM approval
  const handleApproveMom = useCallback(async () => {
    if (!id) return;

    setIsProcessing(true);
    try {
      const response = await meetingApi.approveMom(id);
      if (response.success) {
        // Reload meeting data to get updated status
        await loadMeetingData();
        // Show success message
        alert('MoM approved and JIRA items created successfully!');
      }
    } catch (err) {
      console.error('Failed to approve MoM:', err);
      alert('Failed to approve MoM. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  }, [id, loadMeetingData]);

  // Handle MoM rejection
  const handleRejectMom = useCallback(async (feedback: string) => {
    if (!id) return;

    setIsProcessing(true);
    try {
      const response = await meetingApi.rejectMom(id, feedback);
      if (response.success && response.data) {
        setMom(response.data);
        alert('Feedback sent. MoM will be regenerated.');
      }
    } catch (err) {
      console.error('Failed to reject MoM:', err);
      alert('Failed to send feedback. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  }, [id]);

  // Handle epic updates
  const handleUpdateEpics = useCallback((epics: ProposedEpic[]) => {
    if (mom) {
      setMom({ ...mom, proposedEpics: epics });
    }
  }, [mom]);

  // Handle settings save
  const handleSaveSettings = useCallback(async (newSettings: MeetingSettingsType) => {
    setIsProcessing(true);
    try {
      const response = await meetingApi.updateMeetingSettings(newSettings);
      if (response.success && response.data) {
        setSettings(response.data);
        alert('Settings saved successfully!');
      }
    } catch (err) {
      console.error('Failed to save settings:', err);
      alert('Failed to save settings. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  }, []);

  const getMomStatusBadge = () => {
    if (!meeting) return null;
    const statusConfig = {
      none: { variant: 'default' as const, label: 'No MoM' },
      generating: { variant: 'warning' as const, label: 'Generating' },
      draft: { variant: 'warning' as const, label: 'Draft' },
      sent: { variant: 'info' as const, label: 'Sent' },
      approved: { variant: 'success' as const, label: 'Approved' },
      jira_created: { variant: 'success' as const, label: 'JIRA Created' },
    };
    const config = statusConfig[meeting.momStatus];
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  if (isLoading) {
    return (
      <AppLayout>
        <div className={styles.loading}>
          <div className={styles.spinner} />
          <p>Loading meeting details...</p>
        </div>
      </AppLayout>
    );
  }

  if (error || !meeting) {
    return (
      <AppLayout>
        <div className={styles.error}>
          <p>{error || 'Meeting not found'}</p>
          <Button onClick={() => navigate('/meetings')}>Back to Meetings</Button>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className={styles.container}>
        <div className={styles.header}>
          <Button variant="ghost" onClick={() => navigate('/meetings')}>
            <ArrowLeft size={20} />
            Back to Meetings
          </Button>
          <div className={styles.titleSection}>
            <h1 className={styles.title}>{meeting.title}</h1>
            {getMomStatusBadge()}
          </div>
          <div className={styles.metadata}>
            <span>
              {new Date(meeting.scheduledStart).toLocaleDateString()} •{' '}
              {new Date(meeting.scheduledStart).toLocaleTimeString()}
            </span>
            {meeting.duration && <span>• Duration: {meeting.duration}</span>}
          </div>
        </div>

        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${activeTab === 'mom' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('mom')}
            disabled={!mom}
          >
            <FileText size={18} />
            Minutes of Meeting
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'transcript' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('transcript')}
            disabled={!meeting.hasTranscript}
          >
            <MessageSquare size={18} />
            Transcript
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'approval' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('approval')}
            disabled={!mom || meeting.momStatus === 'approved' || meeting.momStatus === 'jira_created'}
          >
            <GitBranch size={18} />
            Review & Approve
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'settings' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            <SettingsIcon size={18} />
            Settings
          </button>
        </div>

        <div className={styles.content}>
          {activeTab === 'mom' && mom && <MomViewer mom={mom} />}
          
          {activeTab === 'transcript' && transcript && (
            <TranscriptViewer transcript={transcript} />
          )}
          
          {activeTab === 'transcript' && !transcript && (
            <div className={styles.empty}>
              <p>Loading transcript...</p>
            </div>
          )}
          
          {activeTab === 'approval' && mom && (
            <MomApproval
              mom={mom}
              onApprove={handleApproveMom}
              onReject={handleRejectMom}
              onUpdateEpics={handleUpdateEpics}
              isProcessing={isProcessing}
            />
          )}
          
          {activeTab === 'settings' && settings && (
            <MeetingSettings
              settings={settings}
              onSave={handleSaveSettings}
              isSaving={isProcessing}
            />
          )}
        </div>
      </div>
    </AppLayout>
  );
};

export default MeetingDetailPage;
